const ZIP_LOCAL_FILE_HEADER = 0x04034b50;
const ZIP_CENTRAL_DIRECTORY_HEADER = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const ZIP_UTF8_FLAG = 0x0800;
const ZIP_DATA_DESCRIPTOR_FLAG = 0x0008;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8");

export const sanitizeFileName = (value) => {
  const valueWithoutControlCharacters = Array.from(String(value || "archivo"))
    .filter((character) => character.charCodeAt(0) >= 32)
    .join("");
  const safeValue = valueWithoutControlCharacters
    .replace(/[<>:"/\\|?*]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .trim();

  return safeValue || "archivo";
};

const pad2 = (value) => String(value).padStart(2, "0");

export const buildTimestamp = (date = new Date()) =>
  [
    pad2(date.getDate()),
    pad2(date.getMonth() + 1),
    date.getFullYear(),
  ].join("-") +
  " " +
  [pad2(date.getHours()), pad2(date.getMinutes()), pad2(date.getSeconds())].join("-");

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeDownloadPart = (value) => String(value || "").replace(/\s+/g, " ").trim();

const stripFieldPrefixFromLandName = ({ fieldName, landName }) => {
  const normalizedFieldName = normalizeDownloadPart(fieldName);
  let normalizedLandName = normalizeDownloadPart(landName);

  if (!normalizedFieldName || !normalizedLandName) {
    return normalizedLandName;
  }

  if (normalizedFieldName.toLowerCase() === normalizedLandName.toLowerCase()) {
    return "";
  }

  const fieldPrefixPattern = new RegExp(
    `^${escapeRegExp(normalizedFieldName)}(?:\\s*[-\\u2013\\u2014/\\\\|:]\\s*|\\s+)(.+)$`,
    "i"
  );
  let previousLandName = "";

  while (previousLandName !== normalizedLandName) {
    previousLandName = normalizedLandName;
    normalizedLandName = normalizedLandName.replace(fieldPrefixPattern, "$1").trim();
  }

  return normalizedLandName;
};

export const buildLandDownloadName = ({ fieldName, landName }) =>
  stripFieldPrefixFromLandName({ fieldName, landName });

export const buildFieldLandDownloadSubject = ({ fieldName, landName }) => {
  const normalizedFieldName = normalizeDownloadPart(fieldName);
  const landWithoutFieldPrefix = stripFieldPrefixFromLandName({
    fieldName: normalizedFieldName,
    landName,
  });

  return [normalizedFieldName, landWithoutFieldPrefix].filter(Boolean).join(" - ");
};

export const buildLayerDownloadBaseName = ({ layerName, fieldName, landName }) =>
  sanitizeFileName(`${layerName} - ${buildFieldLandDownloadSubject({ fieldName, landName })} - ${buildTimestamp()}`);

export const buildPrescriptionDownloadBaseName = ({ prefix, fieldName, landName }) =>
  sanitizeFileName(`${prefix} - ${buildFieldLandDownloadSubject({ fieldName, landName })} - ${buildTimestamp()}`);

export const downloadBlob = ({ blob, fileName }) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const getZipEntryMimeType = (fileName) => {
  const extension = getFileExtension(fileName).toLowerCase();

  return {
    ".kml": "application/vnd.google-earth.kml+xml",
    ".pdf": "application/pdf",
    ".png": "image/png",
    ".geojson": "application/geo+json",
    ".json": "application/json",
  }[extension] || "application/octet-stream";
};

const crcTable = (() => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let crc = index;

    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }

    table[index] = crc >>> 0;
  }

  return table;
})();

const calculateCrc32 = (bytes) => {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const toDosTime = (date) =>
  ((date.getHours() & 0x1f) << 11) |
  ((date.getMinutes() & 0x3f) << 5) |
  (Math.floor(date.getSeconds() / 2) & 0x1f);

const toDosDate = (date) =>
  (((date.getFullYear() - 1980) & 0x7f) << 9) |
  (((date.getMonth() + 1) & 0x0f) << 5) |
  (date.getDate() & 0x1f);

const pushUint16 = (target, value) => {
  target.push(value & 0xff, (value >>> 8) & 0xff);
};

const pushUint32 = (target, value) => {
  target.push(
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff
  );
};

const pushBytes = (target, bytes) => {
  for (let index = 0; index < bytes.length; index += 1) {
    target.push(bytes[index]);
  }
};

const concatChunks = (chunks) => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.length;
  });

  return output;
};

const createLocalHeader = ({
  versionNeeded,
  flags,
  compressionMethod,
  modTime,
  modDate,
  crc32,
  compressedSize,
  uncompressedSize,
  fileNameBytes,
  extraBytes,
}) => {
  const bytes = [];

  pushUint32(bytes, ZIP_LOCAL_FILE_HEADER);
  pushUint16(bytes, versionNeeded);
  pushUint16(bytes, flags);
  pushUint16(bytes, compressionMethod);
  pushUint16(bytes, modTime);
  pushUint16(bytes, modDate);
  pushUint32(bytes, crc32);
  pushUint32(bytes, compressedSize);
  pushUint32(bytes, uncompressedSize);
  pushUint16(bytes, fileNameBytes.length);
  pushUint16(bytes, extraBytes.length);
  pushBytes(bytes, fileNameBytes);
  pushBytes(bytes, extraBytes);

  return new Uint8Array(bytes);
};

const createCentralDirectoryHeader = ({
  versionMadeBy,
  versionNeeded,
  flags,
  compressionMethod,
  modTime,
  modDate,
  crc32,
  compressedSize,
  uncompressedSize,
  fileNameBytes,
  extraBytes,
  commentBytes,
  diskStart,
  internalAttributes,
  externalAttributes,
  localHeaderOffset,
}) => {
  const bytes = [];

  pushUint32(bytes, ZIP_CENTRAL_DIRECTORY_HEADER);
  pushUint16(bytes, versionMadeBy);
  pushUint16(bytes, versionNeeded);
  pushUint16(bytes, flags);
  pushUint16(bytes, compressionMethod);
  pushUint16(bytes, modTime);
  pushUint16(bytes, modDate);
  pushUint32(bytes, crc32);
  pushUint32(bytes, compressedSize);
  pushUint32(bytes, uncompressedSize);
  pushUint16(bytes, fileNameBytes.length);
  pushUint16(bytes, extraBytes.length);
  pushUint16(bytes, commentBytes.length);
  pushUint16(bytes, diskStart);
  pushUint16(bytes, internalAttributes);
  pushUint32(bytes, externalAttributes);
  pushUint32(bytes, localHeaderOffset);
  pushBytes(bytes, fileNameBytes);
  pushBytes(bytes, extraBytes);
  pushBytes(bytes, commentBytes);

  return new Uint8Array(bytes);
};

const createEndOfCentralDirectory = ({ entryCount, centralDirectorySize, centralDirectoryOffset }) => {
  const bytes = [];

  pushUint32(bytes, ZIP_END_OF_CENTRAL_DIRECTORY);
  pushUint16(bytes, 0);
  pushUint16(bytes, 0);
  pushUint16(bytes, entryCount);
  pushUint16(bytes, entryCount);
  pushUint32(bytes, centralDirectorySize);
  pushUint32(bytes, centralDirectoryOffset);
  pushUint16(bytes, 0);

  return new Uint8Array(bytes);
};

export const createZipWithSingleFile = async ({ fileName, blob }) => {
  const fileBytes = new Uint8Array(await blob.arrayBuffer());
  const fileNameBytes = textEncoder.encode(sanitizeFileName(fileName));
  const now = new Date();
  const crc32 = calculateCrc32(fileBytes);
  const commonHeader = {
    versionNeeded: 20,
    flags: ZIP_UTF8_FLAG,
    compressionMethod: 0,
    modTime: toDosTime(now),
    modDate: toDosDate(now),
    crc32,
    compressedSize: fileBytes.length,
    uncompressedSize: fileBytes.length,
    fileNameBytes,
    extraBytes: new Uint8Array(0),
  };
  const localHeader = createLocalHeader(commonHeader);
  const centralHeader = createCentralDirectoryHeader({
    ...commonHeader,
    versionMadeBy: 20,
    commentBytes: new Uint8Array(0),
    diskStart: 0,
    internalAttributes: 0,
    externalAttributes: 0,
    localHeaderOffset: 0,
  });
  const centralDirectoryOffset = localHeader.length + fileBytes.length;
  const endOfCentralDirectory = createEndOfCentralDirectory({
    entryCount: 1,
    centralDirectorySize: centralHeader.length,
    centralDirectoryOffset,
  });

  return new Blob([concatChunks([localHeader, fileBytes, centralHeader, endOfCentralDirectory])], {
    type: "application/zip",
  });
};

const findEndOfCentralDirectory = (bytes, view) => {
  const minimumOffset = Math.max(0, bytes.length - 22 - 0xffff);

  for (let offset = bytes.length - 22; offset >= minimumOffset; offset -= 1) {
    if (view.getUint32(offset, true) === ZIP_END_OF_CENTRAL_DIRECTORY) {
      return offset;
    }
  }

  return -1;
};

const getFileExtension = (path) => {
  const fileName = String(path || "").replace(/\\/g, "/").split("/").pop() || "";
  const extensionStart = fileName.lastIndexOf(".");

  if (extensionStart <= 0) {
    return "";
  }

  return fileName.slice(extensionStart);
};

const isZipBlob = async (blob) => {
  const bytes = new Uint8Array(await blob.slice(0, 4).arrayBuffer());

  return bytes.length === 4 && new DataView(bytes.buffer).getUint32(0, true) === ZIP_LOCAL_FILE_HEADER;
};

const throwIfJsonErrorBlob = async (blob) => {
  const prefix = textDecoder.decode(new Uint8Array(await blob.slice(0, 64).arrayBuffer())).trim();

  if (!prefix.startsWith("{")) {
    return;
  }

  try {
    const payload = JSON.parse(await blob.text());

    if (payload?.message) {
      throw new Error(payload.message);
    }
  } catch (error) {
    if (error?.message && error.message !== "Unexpected end of JSON input") {
      throw error;
    }
  }
};

const inflateRawBytes = async (bytes) => {
  const DecompressionStreamConstructor = window.DecompressionStream;

  if (!DecompressionStreamConstructor) {
    throw new Error("Este navegador no puede extraer archivos ZIP comprimidos");
  }

  const formats = ["deflate-raw", "deflate"];

  for (const format of formats) {
    try {
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStreamConstructor(format));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    } catch {
      // Try the next browser-supported deflate variant before failing.
    }
  }

  throw new Error("No pudimos extraer el archivo ZIP descargado");
};

export const extractSingleFileFromZip = async ({ blob, baseName }) => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const endOffset = findEndOfCentralDirectory(bytes, view);

  if (endOffset < 0) {
    throw new Error("No pudimos leer el archivo ZIP descargado");
  }

  const entryCount = view.getUint16(endOffset + 10, true);
  const centralDirectoryOffset = view.getUint32(endOffset + 16, true);

  if (entryCount === 0xffff || centralDirectoryOffset === 0xffffffff) {
    throw new Error("El ZIP descargado usa un formato no soportado");
  }

  const files = [];
  let centralOffset = centralDirectoryOffset;

  for (let entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
    if (view.getUint32(centralOffset, true) !== ZIP_CENTRAL_DIRECTORY_HEADER) {
      throw new Error("El directorio del ZIP descargado no es valido");
    }

    const compressionMethod = view.getUint16(centralOffset + 10, true);
    const compressedSize = view.getUint32(centralOffset + 20, true);
    const uncompressedSize = view.getUint32(centralOffset + 24, true);
    const fileNameLength = view.getUint16(centralOffset + 28, true);
    const extraLength = view.getUint16(centralOffset + 30, true);
    const commentLength = view.getUint16(centralOffset + 32, true);
    const localHeaderOffset = view.getUint32(centralOffset + 42, true);
    const nameStart = centralOffset + 46;
    const extraStart = nameStart + fileNameLength;
    const commentStart = extraStart + extraLength;
    const originalName = textDecoder.decode(bytes.slice(nameStart, extraStart));
    const nextCentralOffset = commentStart + commentLength;

    if (compressedSize === 0xffffffff || uncompressedSize === 0xffffffff || localHeaderOffset === 0xffffffff) {
      throw new Error("El ZIP descargado usa entradas demasiado grandes");
    }

    if (!originalName.endsWith("/") && !originalName.endsWith("\\")) {
      files.push({
        compressedSize,
        compressionMethod,
        localHeaderOffset,
        originalName,
      });
    }

    centralOffset = nextCentralOffset;
  }

  if (files.length !== 1) {
    throw new Error("El ZIP descargado contiene mas de un archivo");
  }

  const file = files[0];

  if (view.getUint32(file.localHeaderOffset, true) !== ZIP_LOCAL_FILE_HEADER) {
    throw new Error("El ZIP descargado tiene una entrada invalida");
  }

  const localFileNameLength = view.getUint16(file.localHeaderOffset + 26, true);
  const localExtraLength = view.getUint16(file.localHeaderOffset + 28, true);
  const fileDataStart = file.localHeaderOffset + 30 + localFileNameLength + localExtraLength;
  const fileDataEnd = fileDataStart + file.compressedSize;
  const compressedBytes = bytes.slice(fileDataStart, fileDataEnd);
  const fileBytes = file.compressionMethod === 0
    ? compressedBytes
    : file.compressionMethod === 8
      ? await inflateRawBytes(compressedBytes)
      : null;

  if (!fileBytes) {
    throw new Error("El ZIP descargado usa una compresion no soportada");
  }

  const extension = getFileExtension(file.originalName);
  const fileName = sanitizeFileName(`${baseName}${extension}`);

  return {
    blob: new Blob([fileBytes], { type: getZipEntryMimeType(fileName) }),
    fileName,
  };
};

export const downloadPossiblyZippedSingleFile = async ({ blob, baseName, extension, mimeType }) => {
  if (await isZipBlob(blob)) {
    const singleFile = await extractSingleFileFromZip({ blob, baseName });
    downloadBlob(singleFile);
    return;
  }

  await throwIfJsonErrorBlob(blob);

  downloadBlob({
    blob: new Blob([blob], { type: mimeType || getZipEntryMimeType(`${baseName}.${extension}`) }),
    fileName: sanitizeFileName(`${baseName}.${extension}`),
  });
};

const getRenamedEntryName = ({ originalName, baseName, usedNames }) => {
  const extension = getFileExtension(originalName);
  let candidate = `${baseName}${extension}`;
  let counter = 2;

  while (usedNames.has(candidate.toLowerCase())) {
    candidate = `${baseName}-${counter}${extension}`;
    counter += 1;
  }

  usedNames.add(candidate.toLowerCase());
  return candidate;
};

export const renameZipEntries = async ({ blob, baseName }) => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const endOffset = findEndOfCentralDirectory(bytes, view);

  if (endOffset < 0) {
    throw new Error("No pudimos leer el archivo ZIP descargado");
  }

  const entryCount = view.getUint16(endOffset + 10, true);
  const centralDirectoryOffset = view.getUint32(endOffset + 16, true);

  if (entryCount === 0xffff || centralDirectoryOffset === 0xffffffff) {
    throw new Error("El ZIP descargado usa un formato no soportado");
  }

  const safeBaseName = sanitizeFileName(baseName);
  const usedNames = new Set();
  const localFileChunks = [];
  const centralDirectoryChunks = [];
  let centralOffset = centralDirectoryOffset;
  let outputOffset = 0;
  let renamedEntryCount = 0;

  for (let entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
    if (view.getUint32(centralOffset, true) !== ZIP_CENTRAL_DIRECTORY_HEADER) {
      throw new Error("El directorio del ZIP descargado no es valido");
    }

    const versionMadeBy = view.getUint16(centralOffset + 4, true);
    const versionNeeded = view.getUint16(centralOffset + 6, true);
    const flags = view.getUint16(centralOffset + 8, true);
    const compressionMethod = view.getUint16(centralOffset + 10, true);
    const modTime = view.getUint16(centralOffset + 12, true);
    const modDate = view.getUint16(centralOffset + 14, true);
    const crc32 = view.getUint32(centralOffset + 16, true);
    const compressedSize = view.getUint32(centralOffset + 20, true);
    const uncompressedSize = view.getUint32(centralOffset + 24, true);
    const fileNameLength = view.getUint16(centralOffset + 28, true);
    const extraLength = view.getUint16(centralOffset + 30, true);
    const commentLength = view.getUint16(centralOffset + 32, true);
    const diskStart = view.getUint16(centralOffset + 34, true);
    const internalAttributes = view.getUint16(centralOffset + 36, true);
    const externalAttributes = view.getUint32(centralOffset + 38, true);
    const localHeaderOffset = view.getUint32(centralOffset + 42, true);
    const nameStart = centralOffset + 46;
    const extraStart = nameStart + fileNameLength;
    const commentStart = extraStart + extraLength;
    const originalName = textDecoder.decode(bytes.slice(nameStart, extraStart));
    const centralExtraBytes = bytes.slice(extraStart, commentStart);
    const commentBytes = bytes.slice(commentStart, commentStart + commentLength);
    const nextCentralOffset = commentStart + commentLength;

    if (compressedSize === 0xffffffff || uncompressedSize === 0xffffffff || localHeaderOffset === 0xffffffff) {
      throw new Error("El ZIP descargado usa entradas demasiado grandes");
    }

    const isDirectory = originalName.endsWith("/") || originalName.endsWith("\\");

    if (!isDirectory) {
      if (view.getUint32(localHeaderOffset, true) !== ZIP_LOCAL_FILE_HEADER) {
        throw new Error("El ZIP descargado tiene una entrada invalida");
      }

      const localFileNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
      const localExtraStart = localHeaderOffset + 30 + localFileNameLength;
      const fileDataStart = localExtraStart + localExtraLength;
      const fileDataEnd = fileDataStart + compressedSize;
      const newFlags = (flags | ZIP_UTF8_FLAG) & ~ZIP_DATA_DESCRIPTOR_FLAG;
      const newFileName = getRenamedEntryName({
        originalName,
        baseName: safeBaseName,
        usedNames,
      });
      const newFileNameBytes = textEncoder.encode(newFileName);
      const localExtraBytes = bytes.slice(localExtraStart, fileDataStart);
      const fileDataBytes = bytes.slice(fileDataStart, fileDataEnd);
      const localHeader = createLocalHeader({
        versionNeeded,
        flags: newFlags,
        compressionMethod,
        modTime,
        modDate,
        crc32,
        compressedSize,
        uncompressedSize,
        fileNameBytes: newFileNameBytes,
        extraBytes: localExtraBytes,
      });
      const centralHeader = createCentralDirectoryHeader({
        versionMadeBy,
        versionNeeded,
        flags: newFlags,
        compressionMethod,
        modTime,
        modDate,
        crc32,
        compressedSize,
        uncompressedSize,
        fileNameBytes: newFileNameBytes,
        extraBytes: centralExtraBytes,
        commentBytes,
        diskStart,
        internalAttributes,
        externalAttributes,
        localHeaderOffset: outputOffset,
      });

      localFileChunks.push(localHeader, fileDataBytes);
      centralDirectoryChunks.push(centralHeader);
      outputOffset += localHeader.length + fileDataBytes.length;
      renamedEntryCount += 1;
    }

    centralOffset = nextCentralOffset;
  }

  if (renamedEntryCount < 1) {
    throw new Error("El ZIP descargado no contiene archivos para renombrar");
  }

  const centralDirectoryBytes = concatChunks(centralDirectoryChunks);
  const centralDirectoryStart = outputOffset;
  const endOfCentralDirectory = createEndOfCentralDirectory({
    entryCount: renamedEntryCount,
    centralDirectorySize: centralDirectoryBytes.length,
    centralDirectoryOffset: centralDirectoryStart,
  });

  return new Blob(
    [concatChunks([...localFileChunks, centralDirectoryBytes, endOfCentralDirectory])],
    { type: "application/zip" }
  );
};
